import { Home, Car, Heart, Umbrella, Shield, ArrowRight, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";

export const PersonalInsurancePage = () => {
  const insuranceProducts = [
    {
      icon: Home,
      title: "Homeowners Insurance",
      description: "Protect your home and belongings from damage, theft, and liability claims",
      bgColor: "bg-blue-50",
      features: [
        "Dwelling coverage for structure damage",
        "Personal property protection",
        "Liability coverage",
        "Additional living expenses",
      ],
    },
    {
      icon: Car,
      title: "Auto Insurance",
      description: "Comprehensive coverage for your vehicles with competitive rates",
      bgColor: "bg-slate-100",
      features: [
        "Liability coverage",
        "Collision and comprehensive",
        "Uninsured motorist protection",
        "Roadside assistance options",
      ],
    },
    {
      icon: Umbrella,
      title: "Umbrella Insurance",
      description: "Extra liability protection beyond your standard policy limits",
      bgColor: "bg-sky-50",
      features: [
        "Extended liability coverage",
        "Protection for multiple policies",
        "Coverage for legal defense costs",
        "Affordable premium rates",
      ],
    },
    {
      icon: Heart,
      title: "Life Insurance",
      description: "Financial security for your loved ones when they need it most",
      bgColor: "bg-indigo-50",
      features: [
        "Term life insurance options",
        "Permanent life insurance",
        "Income replacement protection",
        "Estate planning solutions",
      ],
    },
  ];

  const additionalCoverage = [
    { name: "Renters Insurance", bgColor: "bg-blue-50" },
    { name: "Condo Insurance", bgColor: "bg-slate-100" },
    { name: "Motorcycle Insurance", bgColor: "bg-sky-50" },
    { name: "Boat & Watercraft Insurance", bgColor: "bg-indigo-50" },
    { name: "RV & Recreational Vehicle Insurance", bgColor: "bg-blue-50" },
    { name: "Valuable Items Insurance", bgColor: "bg-slate-100" },
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary to-blue-700 text-white section-padding overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1588979355313-6711a095465f?w=1600&q=80')",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary/95 to-blue-700/90"></div>
        </div>
        <div className="container-custom relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <Shield className="h-20 w-20 mx-auto mb-6 text-blue-100" />
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Personal Insurance Solutions</h1>
            <p className="text-xl text-blue-100 mb-8">
              Comprehensive protection for you, your family, and your most valuable assets
            </p>
            <Link to="/contact">
              <Button size="lg" variant="secondary" className="bg-white text-primary hover:bg-gray-100 font-semibold px-8">
                Get Your Free Quote
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Main Insurance Products */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Core Personal Insurance Products
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Protect what matters most with tailored insurance solutions designed for your personal needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {insuranceProducts.map((product, index) => (
              <Card key={index} className={`card-hover border-2 hover:border-primary ${product.bgColor}`}>
                <CardHeader>
                  <div className="bg-primary/10 w-14 h-14 rounded-lg flex items-center justify-center mb-4">
                    <product.icon className="h-7 w-7 text-primary" />
                  </div>
                  <CardTitle className="text-2xl">{product.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-6">{product.description}</p>
                  <ul className="space-y-3">
                    {product.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Coverage */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-8 text-foreground">
              Additional Personal Coverage Options
            </h2>
            <p className="text-lg text-muted-foreground text-center mb-12">
              We offer specialized insurance products to protect your unique lifestyle and assets
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {additionalCoverage.map((item, index) => (
                <Card key={index} className={`card-hover hover:border-primary border-2 ${item.bgColor}`}>
                  <CardContent className="pt-6">
                    <div className="flex items-center space-x-3">
                      <Shield className="h-6 w-6 text-primary flex-shrink-0" />
                      <h3 className="text-lg font-semibold text-foreground">{item.name}</h3>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Visual Section - Home & Auto */}
      <section className="section-padding bg-gradient-to-b from-white to-gray-50">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-foreground">
            Protecting What You Love Most
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {/* Home Insurance Visual */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
              <img
                src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800&q=80"
                alt="Beautiful modern home"
                className="w-full h-[400px] object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent flex items-end">
                <div className="p-8 text-white">
                  <Home className="h-12 w-12 mb-4" />
                  <h3 className="text-3xl font-bold mb-2">Homeowners Insurance</h3>
                  <p className="text-gray-200 text-lg">Protect your most valuable investment</p>
                </div>
              </div>
            </div>

            {/* Auto Insurance Visual */}
            <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
              <img
                src="https://images.unsplash.com/photo-1601929862217-f1bf94503333?w=800&q=80"
                alt="Luxury car on the road"
                className="w-full h-[400px] object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent flex items-end">
                <div className="p-8 text-white">
                  <Car className="h-12 w-12 mb-4" />
                  <h3 className="text-3xl font-bold mb-2">Auto Insurance</h3>
                  <p className="text-gray-200 text-lg">Comprehensive coverage on the road</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl p-8 md:p-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                  Why Choose Us for Personal Insurance?
                </h2>
                <div className="space-y-4">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-lg mb-1 text-foreground">Compare Multiple Carriers</h3>
                      <p className="text-muted-foreground">
                        As an independent agency, we shop multiple insurance companies to find you the best coverage at the best price
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-lg mb-1 text-foreground">Personalized Recommendations</h3>
                      <p className="text-muted-foreground">
                        We take time to understand your unique needs and recommend coverage that truly protects you
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-lg mb-1 text-foreground">Bundle and Save</h3>
                      <p className="text-muted-foreground">
                        Combine multiple policies to maximize your savings while maintaining comprehensive protection
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-lg mb-1 text-foreground">Claims Support</h3>
                      <p className="text-muted-foreground">
                        We're here to help you through the claims process, providing guidance and advocacy when you need it most
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-8">
                <h3 className="text-2xl font-bold mb-4 text-center text-foreground">Ready to Get Protected?</h3>
                <p className="text-muted-foreground text-center mb-6">
                  Get a personalized quote in minutes and discover how affordable comprehensive coverage can be
                </p>
                <Link to="/contact">
                  <Button size="lg" className="w-full font-semibold">
                    Request Your Free Quote
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <div className="mt-6 text-center text-sm text-muted-foreground">
                  <p>Serving MN, WI, IA, ND, SD, TN, and AZ</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
